// @oasis-permission-public: open to any signed-in Oasis user
import { Icon } from "@mirage-web/icons";
import { createFileRoute, Link } from "@tanstack/react-router";
import { decodeJwt } from "jose";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { getApiError } from "~/lib/network/errors";
import { queryClient } from "~/lib/query-client";
import { useAuthStore } from "~/modules/auth/auth-store";
import { loginCredentials } from "~/modules/auth/credentials";
import { getRoleDisplayName } from "~/modules/auth/roles";
import { createEmployeeLinkMutation } from "~/modules/employee/mutations";
import { UserIdLookupField, useUserIdLookup } from "~/modules/lookup/components/user-id-lookup";
import { sendMyTestflightInviteMutation } from "~/modules/testflight/mutations";
import { myTestflightStatusQuery } from "~/modules/testflight/queries";
import { TESTFLIGHT_INVITE_ACTION_MESSAGES } from "~/modules/testflight/schemas";

export const Route = createFileRoute("/_dash/home")({
  component: RouteComponent,
});

function getCurrentRole(): string | null {
  try {
    const credentials = loginCredentials.get();
    if (!credentials) {
      return null;
    }

    const decoded = decodeJwt(credentials.accessToken);
    return (decoded as { role: string })?.role || null;
  } catch (error) {
    console.error("Failed to decode access token:", error);
    return null;
  }
}

function formatTestflightState(state: string) {
  return state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
}

function hasAcceptedInvite(state: string | null | undefined) {
  const normalized = state?.toUpperCase();
  return normalized === "ACCEPTED" || normalized === "INSTALLED";
}

function TestflightStatus({
  status,
}: {
  status: NonNullable<ReturnType<typeof myTestflightStatusQuery.useQuery>["data"]>;
}) {
  const { mutate: sendInvite, isPending } = sendMyTestflightInviteMutation.useMutation({
    onSuccess: (result) => {
      toast.success(TESTFLIGHT_INVITE_ACTION_MESSAGES[result.action]);
    },
    onError: (inviteError) => {
      const apiError = getApiError(inviteError);
      toast.error(
        apiError?.code === "app_store_connect_rejected"
          ? `App Store Connect rejected the request: ${apiError.message}`
          : "Failed to send TestFlight invitation"
      );
      console.error("TestFlight invite error:", inviteError);
    },
  });

  const inviteHint = (
    <p className="text-xs text-muted-foreground">
      The invitation email will be sent to your nocapinc.com email, feel free to forward it to any
      email on your phone and accept the invitation from there.
    </p>
  );

  if (status.isMember) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span>In the Captions – Employees group</span>
          {status.state && <Badge variant="secondary">{formatTestflightState(status.state)}</Badge>}
          {hasAcceptedInvite(status.state) ? (
            <span className="text-muted-foreground">Open the TestFlight app to install builds</span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={isPending}
              onClick={() => sendInvite({ params: {} })}
            >
              {isPending ? "Sending…" : "Resend invitation"}
            </Button>
          )}
        </div>
        {!hasAcceptedInvite(status.state) && inviteHint}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span>Not in the group yet — the invitation goes to {status.email}</span>
        <Button size="sm" disabled={isPending} onClick={() => sendInvite({ params: {} })}>
          {isPending ? "Sending…" : "Send invitation"}
        </Button>
      </div>
      {inviteHint}
    </div>
  );
}

function AttachAccountControl({ mainUserId }: { mainUserId: string }) {
  const lookup = useUserIdLookup();
  const { mutate: attach, isPending } = createEmployeeLinkMutation.useMutation({
    onSuccess: () => {
      toast.success("Account attached");
      lookup.reset();
      myTestflightStatusQuery.invalidateQuery(queryClient);
    },
    onError: (error) => {
      const apiError = getApiError(error);
      toast.error(
        apiError?.message
          ? `Failed to attach account: ${apiError.message}`
          : "Failed to attach account"
      );
      console.error("Attach account error:", error);
    },
  });

  return (
    <div className="space-y-2 border-t pt-3">
      <p className="font-medium">Attach an account to your main account</p>
      <UserIdLookupField lookup={lookup} disabled={isPending} />
      <Button
        size="sm"
        disabled={isPending || !lookup.resolvedUser}
        onClick={() => {
          if (lookup.resolvedUser) {
            attach({
              params: {
                flaggedUserId: lookup.resolvedUser.userId,
                mainEmployeeUserId: mainUserId,
              },
            });
          }
        }}
      >
        {isPending ? "Attaching…" : "Attach"}
      </Button>
    </div>
  );
}

function AccountSections({
  user,
  currentRole,
}: {
  user: { name: string; email: string; permissions: string[] };
  currentRole: string | null;
}) {
  const { data: status, isError } = myTestflightStatusQuery.useQuery({ retry: 1 });
  const canAttach = user.permissions.includes("internal-account:write");

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Card className="min-w-0 gap-4">
        <CardHeader>
          <h2 className="text-lg font-semibold">Info</h2>
        </CardHeader>
        <CardContent>
          <dl className="space-y-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Name</dt>
              <dd className="mt-1 break-words font-medium">{user.name}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Email</dt>
              <dd className="mt-1 break-all">{user.email}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Current role</dt>
              <dd className="mt-1">{getRoleDisplayName(currentRole)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
      <Card className="min-w-0 gap-4">
        <CardHeader>
          <h2 className="text-lg font-semibold">TestFlight</h2>
        </CardHeader>
        <CardContent className="text-sm">
          {status ? (
            <TestflightStatus status={status} />
          ) : (
            <p className="text-muted-foreground">
              {isError ? "Unable to load TestFlight status." : "Loading TestFlight status…"}
            </p>
          )}
        </CardContent>
      </Card>
      <Card className="min-w-0 gap-4 md:col-span-2">
        <CardHeader>
          <h2 className="text-lg font-semibold">Attached accounts</h2>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {status ? (
            <>
              {status.linkedAccounts.length === 0 ? (
                <p className="text-muted-foreground">No attached accounts.</p>
              ) : (
                <ul
                  aria-label="Attached accounts"
                  tabIndex={0}
                  className="grid max-h-64 grid-cols-1 gap-2 overflow-y-auto rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:grid-cols-2"
                >
                  {status.linkedAccounts.map((account) => (
                    <li key={account.userId} className="break-all rounded-md border px-3 py-2">
                      {account.email ?? account.userId}
                    </li>
                  ))}
                </ul>
              )}
              {canAttach && <AttachAccountControl mainUserId={status.userId} />}
            </>
          ) : (
            <p className="text-muted-foreground">
              {isError ? "Unable to load attached accounts." : "Loading attached accounts…"}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RouteComponent() {
  const [user] = useAuthStore((s) => [s.user]);
  const currentRole = getCurrentRole();

  return (
    <div className="space-y-4 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold">Welcome to the Oasis 🏝️</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Link
          to="/videos"
          className="group p-4 border rounded-lg hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
        >
          <Icon
            name="video-camera"
            className="size-6 mb-2 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100"
          />
          <h3 className="font-semibold text-sm">Videos</h3>
          <p className="text-xs text-muted-foreground mt-1">Mirage video outputs</p>
        </Link>

        <Link
          to="/users"
          className="group p-4 border rounded-lg hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
        >
          <Icon
            name="person"
            className="size-6 mb-2 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100"
          />
          <h3 className="font-semibold text-sm">Users</h3>
          <p className="text-xs text-muted-foreground mt-1">Search and view user information</p>
        </Link>

        <Link
          to="/organizations"
          className="group p-4 border rounded-lg hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
        >
          <Icon
            name="teams"
            className="size-6 mb-2 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100"
          />
          <h3 className="font-semibold text-sm">Organizations</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Manage organization details and members
          </p>
        </Link>
        <Link
          to="/credits"
          className="group p-4 border rounded-lg hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
        >
          <Icon
            name="credit-card"
            className="size-6 mb-2 text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100"
          />
          <h3 className="font-semibold text-sm">Credits</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Grant credits to users and organizations
          </p>
        </Link>
      </div>

      {user && <AccountSections user={user} currentRole={currentRole} />}
    </div>
  );
}
